import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { cn } from '~/common/utils/styleUtil';
import type { IArticleTranslatedTextDto } from '~/dtos/hub/hub.models.generated';

export interface ArticleSecondaryTextProps {
  articleNumber: string;
  brand?: string;
  rohstoffHerkunft?: IArticleTranslatedTextDto[];
  lineClamp: 1 | 3 | 5;
  tile?: boolean;
}

export function ArticleSecondaryText({
  articleNumber,
  brand,
  rohstoffHerkunft,
  lineClamp,
  tile = false
}: Readonly<ArticleSecondaryTextProps>) {
  const { t } = useTranslation();

  const textInfos = useMemo(() => {
    const textList: { label: string; value: string }[] = [{ label: t('Art. Nr.'), value: articleNumber }];
    if (brand && brand?.trim().length > 0) {
      textList.push({ label: t('Marke'), value: brand });
    }
    if (rohstoffHerkunft && rohstoffHerkunft.length > 0) {
      const maxItems = 3; //Nach Anforderung nur 3 anzeigen
      const selectedItems = rohstoffHerkunft.slice(0, maxItems).map(x => x.text);
      const suffix = rohstoffHerkunft.length > maxItems ? ' ...' : '';

      textList.push({
        label: t('Rohstoffherkunft'),
        value: selectedItems.join(', ') + suffix
      });
    }

    return textList;
  }, [articleNumber, brand, rohstoffHerkunft, t]);

  return !tile ? (
    <p
      className={cn(
        lineClamp === 5 && 'line-clamp-5',
        lineClamp === 3 && 'line-clamp-3',
        lineClamp === 1 && 'line-clamp-1'
      )}
    >
      {textInfos.map((textInfo, index) => (
        <span key={textInfo.label} className={'text-secondary text-sm '}>
          <span className={''} data-testid={`articleSecondary-${articleNumber}-${index}-text`}>
            {textInfo.label}
          </span>
          :&nbsp;{textInfo.value}
          {index < textInfos.length - 1 ? <span>{' · '}</span> : null}
        </span>
      ))}
    </p>
  ) : (
    <div className={'flex gap-4 flex-wrap pt-2 pb-2'}>
      {textInfos.map((textInfo, index) => (
        <div className={'text-sm flex flex-col '} key={textInfo.label}>
          <div>{textInfo.label}</div>
          <div className={'text-secondary'} data-testid={`articleSecondary-${articleNumber}-${index}-text`}>
            {textInfo.value}
          </div>
        </div>
      ))}
    </div>
  );
}
